import React, { useEffect, useState } from 'react';
import { APP_URL, API_URL } from '../env';
import SectionSpyNav from '../components/SectionSpyNav';

const MockDashboard: React.FC = () => (
  <div style={{
    background: '#1e293b',
    border: '1px solid rgba(148,163,184,0.15)',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 480,
    position: 'relative',
    overflow: 'hidden',
    animation: 'pulse-glow 3s ease-in-out infinite',
  }}>
    {/* Header */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
      <div>
        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 4 }}>PRETTIFAI Dashboard</div>
        <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>Invoice Scanner</div>
      </div>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: 'linear-gradient(135deg, #2563eb, #22c55e)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.8rem',
      }}>AI</div>
    </div>

    {/* Scan line */}
    <div style={{
      position: 'absolute',
      left: 0, right: 0, height: 2,
      background: 'linear-gradient(90deg, transparent, #22c55e, transparent)',
      animation: 'scan 2s linear infinite',
      zIndex: 2,
      opacity: 0.7,
    }} />

    {/* Invoice Cards */}
    {[
      { vendor: 'AWS', amount: '$1,240.00', date: 'Apr 2024', tag: 'Cloud', color: '#f97316' },
      { vendor: 'Stripe', amount: '$89.00', date: 'Apr 2024', tag: 'Payment', color: '#f59e0b' },
      { vendor: 'Vercel', amount: '$20.00', date: 'Apr 2024', tag: 'Hosting', color: '#f97316' },
      { vendor: 'OpenAI', amount: '$156.40', date: 'Apr 2024', tag: 'AI', color: '#f97316' },
    ].map((item, i) => (
      <div key={i} style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        background: '#0f172a',
        borderRadius: 8,
        marginBottom: 8,
        border: '1px solid rgba(148,163,184,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Shimmer overlay */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.04) 50%, transparent 100%)',
          animation: `shimmer ${1.5 + i * 0.3}s ease-in-out infinite`,
          transform: 'translateX(-100%)',
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: item.color,
          }} />
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{item.vendor}</div>
            <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{item.date}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: '0.65rem', padding: '2px 6px',
            background: `${item.color}22`,
            color: item.color,
            borderRadius: 4, fontWeight: 600,
          }}>{item.tag}</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>{item.amount}</span>
        </div>
      </div>
    ))}

    {/* Summary */}
    <div style={{
      marginTop: 16,
      padding: '12px 14px',
      background: 'linear-gradient(135deg, rgba(37,99,235,0.15), rgba(34,197,94,0.15))',
      borderRadius: 8,
      border: '1px solid rgba(34,197,94,0.2)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Total Extracted</span>
      <span style={{ fontSize: '1rem', fontWeight: 800, background: 'linear-gradient(135deg, #2563eb, #22c55e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        $1,505.40
      </span>
    </div>
  </div>
);

const features = [
  {
    icon: '📥',
    title: 'Auto-Detection',
    desc: 'PRETTIFAI continuously monitors your Gmail for new invoices, receipts, and billing emails — no manual searching required.',
  },
  {
    icon: '🤖',
    title: 'AI Extraction',
    desc: 'Powered by advanced AI, PRETTIFAI accurately extracts vendor names, amounts, dates, and line items from any invoice format.',
  },
  {
    icon: '📊',
    title: 'Smart Organization',
    desc: 'Invoices are automatically categorized, tagged, and organized into a clean dashboard. Filter, search, and export in seconds.',
  },
  {
    icon: '🔗',
    title: 'Accountant Portal',
    desc: 'Share a dedicated portal with your accountant. They get read-only access to organized data — no more email chains.',
  },
];

const steps = [
  {
    num: '01',
    title: 'Connect Your Gmail',
    desc: 'Securely connect your Google account with one click. PRETTIFAI uses OAuth2 — we never see your password.',
  },
  {
    num: '02',
    title: 'AI Scans & Extracts',
    desc: 'Our AI goes to work immediately, finding every invoice and receipt in your inbox and extracting the key data.',
  },
  {
    num: '03',
    title: 'Everything Organized',
    desc: 'View your clean dashboard, export to CSV/Excel, share with your accountant, and never lose an invoice again.',
  },
];

const HomePage: React.FC = () => {
  const [trialDocs, setTrialDocs] = useState<number>(40);

  useEffect(() => {
    fetch(`${API_URL}/config/trial`)
      .then(r => r.json())
      .then(data => { if (data?.trialDocs) setTrialDocs(data.trialDocs); })
      .catch(() => {});
  }, []);

  const scrollToHowItWorks = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('how-it-works');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main>
      <SectionSpyNav />
      {/* Hero */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: '120px 0 80px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background glow */}
        <div style={{
          position: 'absolute',
          top: '20%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800, height: 400,
          background: 'radial-gradient(ellipse at center, rgba(37,99,235,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div className="container" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 64,
          alignItems: 'center',
        }}>
          {/* Text */}
          <div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 16px',
              background: 'rgba(37,99,235,0.15)',
              border: '1px solid rgba(37,99,235,0.3)',
              borderRadius: 100,
              fontSize: '0.8rem',
              color: '#93c5fd',
              fontWeight: 500,
              marginBottom: 24,
            }}>
              <span>✨</span> AI-Powered Finance Document Management
            </div>
            <h1 style={{
              fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
              fontWeight: 900,
              lineHeight: 1.15,
              marginBottom: 24,
              letterSpacing: '-0.02em',
            }}>
              Stop chasing documents.<br />
              <span className="gradient-text">Let PRETTIFAI do it.</span>
            </h1>
            <p style={{
              fontSize: '1.15rem',
              color: '#94a3b8',
              lineHeight: 1.8,
              marginBottom: 40,
              maxWidth: 520,
            }}>
              PRETTIFAI brings clarity and control to your business finances. It automatically tracks, classifies, and organizes every document—including IRS records—with precise, enterprise-grade accuracy. A proactive AI assistant streamlines your financial workflow, and licensed CPAs provide expert support whenever you choose to share your data.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
              <a href={APP_URL} className="btn-primary" style={{ fontSize: '1.05rem', padding: '16px 32px' }}>
                Get Started Free →
              </a>
              <a href="#how-it-works" onClick={scrollToHowItWorks} className="btn-secondary">
                See how it works ↓
              </a>
            </div>
            <p style={{ marginTop: 24, fontSize: '0.85rem', color: '#64748b' }}>
              No credit card required • {trialDocs} free documents included
            </p>
          </div>

          {/* Dashboard Visual */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <MockDashboard />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" style={{ padding: '100px 0', background: 'rgba(30,41,59,0.3)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, marginBottom: 16, letterSpacing: '-0.02em' }}>
              Everything you need to manage finance documents
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '1.1rem', maxWidth: 560, margin: '0 auto' }}>
              PRETTIFAI handles the entire document workflow from detection to organization.
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 24,
          }}>
            {features.map((f, i) => (
              <div key={i} style={{
                background: '#1e293b',
                border: '1px solid rgba(148,163,184,0.15)',
                borderRadius: 16,
                padding: 32,
                transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                cursor: 'default',
              }}
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLDivElement;
                  el.style.transform = 'translateY(-4px)';
                  el.style.borderColor = 'rgba(37,99,235,0.3)';
                  el.style.boxShadow = '0 8px 32px rgba(37,99,235,0.15)';
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLDivElement;
                  el.style.transform = 'none';
                  el.style.borderColor = 'rgba(148,163,184,0.15)';
                  el.style.boxShadow = 'none';
                }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 20 }}>{f.icon}</div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 12 }}>{f.title}</h3>
                <p style={{ color: '#94a3b8', lineHeight: 1.7, fontSize: '0.95rem' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" style={{ padding: '100px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, marginBottom: 16, letterSpacing: '-0.02em' }}>
              Up and running in minutes
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '1.1rem', maxWidth: 500, margin: '0 auto' }}>
              Three simple steps to never lose a document again.
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 32,
            position: 'relative',
          }}>
            {steps.map((step, i) => (
              <div key={i} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                position: 'relative',
              }}>
                <div style={{
                  fontSize: '3rem',
                  fontWeight: 900,
                  background: 'var(--primary-gradient)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1,
                  marginBottom: 20,
                  fontVariantNumeric: 'tabular-nums',
                }}>{step.num}</div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 12 }}>{step.title}</h3>
                <p style={{ color: '#94a3b8', lineHeight: 1.7, fontSize: '0.95rem' }}>{step.desc}</p>
                {i < steps.length - 1 && (
                  <div style={{
                    position: 'absolute',
                    right: -16,
                    top: 20,
                    fontSize: '1.5rem',
                    color: '#334155',
                    display: 'none',
                  }} className="step-arrow">→</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / compliance */}
      <section id="trust" style={{ padding: '100px 0', background: '#0b1220' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, marginBottom: 16, letterSpacing: '-0.02em' }}>
              Independently audited security
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '1.1rem', maxWidth: 560, margin: '0 auto' }}>
              Download our signed SOC 2 Type I report and LOV letter.
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 24,
            maxWidth: 800,
            margin: '0 auto',
          }}>
            {[
              {
                title: 'SOC 2 Type I Report',
                desc: 'Final signed report covering Prettifai’s security controls.',
                href: '/docs/prettifai-soc2-type-i-report.pdf',
              },
              {
                title: 'LOV Letter',
                desc: 'Letter of Validation (LOV-34) accompanying our SOC 2 attestation.',
                href: '/docs/prettifai-lov-34.pdf',
              },
            ].map((doc) => (
              <a
                key={doc.href}
                href={doc.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  background: '#1e293b',
                  border: '1px solid rgba(148,163,184,0.15)',
                  borderRadius: 16,
                  padding: 28,
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget;
                  el.style.transform = 'translateY(-4px)';
                  el.style.borderColor = 'rgba(37,99,235,0.45)';
                  el.style.boxShadow = '0 12px 32px rgba(37,99,235,0.15)';
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget;
                  el.style.transform = 'none';
                  el.style.borderColor = 'rgba(148,163,184,0.15)';
                  el.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#22c55e',
                  marginBottom: 12,
                }}>PDF</div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 10 }}>{doc.title}</h3>
                <p style={{ color: '#94a3b8', lineHeight: 1.7, fontSize: '0.95rem', marginBottom: 16 }}>{doc.desc}</p>
                <span style={{ color: '#60a5fa', fontSize: '0.9rem', fontWeight: 600 }}>View document →</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section style={{
        padding: '80px 0',
        background: 'linear-gradient(135deg, rgba(37,99,235,0.15), rgba(34,197,94,0.1))',
        borderTop: '1px solid rgba(148,163,184,0.1)',
        borderBottom: '1px solid rgba(148,163,184,0.1)',
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, marginBottom: 20, letterSpacing: '-0.02em' }}>
            Ready to automate your finance documents?
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '1.1rem', marginBottom: 40, maxWidth: 480, margin: '0 auto 40px' }}>
            Join hundreds of businesses saving hours every month with PRETTIFAI.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={APP_URL} className="btn-primary" style={{ fontSize: '1.05rem', padding: '16px 36px' }}>
              Start for Free →
            </a>
            <a href="/pricing" className="btn-secondary">
              View Pricing
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
